import { AuthClient, SyncError, type AuthUser } from "@app/storage";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { loadSyncUrl } from "../lib/sync-metadata.js";
import {
  accountGeneration,
  bumpAccountGeneration,
  clearAccountSyncState,
  withAccountTransition,
} from "../lib/account-session.js";
import { activeGoogleClientId, googleConfigured, googleIdentityOAuthConfig } from "../lib/google-config.js";
import { runOAuthFlow, GoogleOAuthError } from "../lib/google-oauth/index.js";
import { exchangeAuthCode } from "../lib/google-oauth/exchange.js";
import { tr } from "../lib/tr.js";
import { toast } from "./toast.store.js";

const K_ACCESS = "auth_access_token";
const K_REFRESH = "auth_refresh_token";
const K_EMAIL = "auth_email";

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  busy: boolean;
  error: string | null;
  loaded: boolean;
  load: () => Promise<void>;
  signup: (email: string, password: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  /** Sign in / sign up via Google identity. Mints OUR account tokens — does NOT
   *  request or use Google Calendar scopes (that's a separate connection). */
  loginWithGoogle: () => Promise<boolean>;
  /** Whether Google sign-in can be offered in this build (a client id is set). */
  googleAvailable: () => boolean;
  logout: () => Promise<void>;
  /** Permanently delete the account on the server, then sign out locally.
   *  Local on-device data is left intact. Returns whether deletion succeeded. */
  deleteAccount: () => Promise<boolean>;
  /** Email a password-reset link (no sign-in needed). Resolves true on a sent
   *  request — the message is intentionally vague (no account enumeration). */
  requestPasswordReset: (email: string) => Promise<boolean>;
  /** Set a new password using the token from the reset email. */
  resetPassword: (token: string, password: string) => Promise<boolean>;
  /** Resend the verification email for the signed-in account. */
  resendVerification: () => Promise<void>;
  /** Refresh hook for the sync client: returns a fresh access token, or null
   *  (and signs out) if the refresh token is gone/expired. */
  refreshAccess: (opts?: { syncLaneHeld?: boolean }) => Promise<string | null>;
}

async function serverUrl(): Promise<string> {
  return loadSyncUrl();
}

/**
 * Account/session state for the commercial backend (F-PROD). Tokens are stored
 * in settings on THIS device only and are NEVER added to `gatherPreferences()`,
 * so they never enter a backup (same treatment as the old sync token).
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  busy: false,
  error: null,
  loaded: false,

  async load() {
    const s = getServices().settings;
    const accessToken = (await s.get(K_ACCESS)) || null;
    const refreshToken = (await s.get(K_REFRESH)) || null;
    const email = (await s.get(K_EMAIL)) || null;
    set({
      accessToken,
      refreshToken,
      user: email ? { id: "", email, created_at: "" } : null,
      loaded: true,
    });
    // The persisted user is just the email — refresh the full record (incl.
    // `email_verified`, which drives the verify banner) in the background.
    if (accessToken) void refreshUser(set, get, accessToken);
  },

  async signup(email, password) {
    return authenticate(set, "signup", email, password);
  },

  async login(email, password) {
    return authenticate(set, "login", email, password);
  },

  googleAvailable() {
    return googleConfigured();
  },

  async loginWithGoogle() {
    if (!googleConfigured()) {
      toast.error(tr("toast.auth.googleNotConfigured"));
      return false;
    }
    const url = await serverUrl();
    if (!url) {
      toast.error(tr("toast.sync.setUrlFirst"));
      return false;
    }
    set({ busy: true, error: null });
    try {
      // Identity-only consent (no Calendar, no offline) → an OIDC ID token we
      // exchange with our server for OUR account tokens.
      const result = await runOAuthFlow(googleIdentityOAuthConfig(), {
        offline: false,
        prompt: "select_account",
      });
      const tokens = await exchangeAuthCode({
        serverUrl: url,
        clientId: activeGoogleClientId(),
        result,
      });
      if (!tokens.id_token) throw new GoogleOAuthError("Google didn't return an identity token.");
      const res = await new AuthClient(url).googleSignIn(tokens.id_token);
      await establishAccount(set, res);
      toast.success(tr("toast.auth.googleSignedIn"));
      return true;
    } catch (e) {
      const msg =
        e instanceof SyncError || e instanceof GoogleOAuthError || e instanceof Error
          ? e.message
          : String(e);
      set({ error: msg });
      toast.error(msg);
      return false;
    } finally {
      set({ busy: false });
    }
  },

  async logout() {
    const { refreshToken } = get();
    const url = await serverUrl();
    if (url && refreshToken) {
      try {
        await new AuthClient(url).logout(refreshToken);
      } catch {
        /* best-effort — clear locally regardless */
      }
    }
    await clearLocalAccount(set);
  },

  async deleteAccount() {
    const url = await serverUrl();
    const token = get().accessToken;
    if (!url || !token) return false;
    set({ busy: true, error: null });
    try {
      const client = new AuthClient(url);
      try {
        await client.deleteAccount(token);
      } catch (e) {
        // Access token may have expired between sign-in and this action.
        if (e instanceof SyncError && e.status === 401) {
          const fresh = await get().refreshAccess();
          if (!fresh) throw new SyncError("Your session expired. Sign in again, then delete.");
          await client.deleteAccount(fresh);
        } else {
          throw e;
        }
      }
      await clearLocalAccount(set);
      toast.success(tr("toast.auth.accountDeleted"));
      return true;
    } catch (e) {
      const msg = e instanceof SyncError ? e.message : e instanceof Error ? e.message : String(e);
      set({ error: msg });
      toast.error(msg);
      return false;
    } finally {
      set({ busy: false });
    }
  },

  async requestPasswordReset(email) {
    const url = await serverUrl();
    if (!url) {
      toast.error(tr("toast.sync.setUrlFirst"));
      return false;
    }
    try {
      await new AuthClient(url).requestPasswordReset(email.trim());
      toast.success(tr("toast.auth.resetLinkSent"));
      return true;
    } catch (e) {
      toast.error(e instanceof SyncError ? e.message : e instanceof Error ? e.message : String(e));
      return false;
    }
  },

  async resetPassword(token, password) {
    const url = await serverUrl();
    if (!url) {
      toast.error(tr("toast.sync.setUrlFirst"));
      return false;
    }
    try {
      await new AuthClient(url).resetPassword(token, password);
      toast.success(tr("toast.auth.passwordUpdated"));
      return true;
    } catch (e) {
      toast.error(e instanceof SyncError ? e.message : e instanceof Error ? e.message : String(e));
      return false;
    }
  },

  async resendVerification() {
    const url = await serverUrl();
    const token = get().accessToken;
    if (!url || !token) return;
    const client = new AuthClient(url);
    try {
      try {
        await client.resendVerification(token);
      } catch (e) {
        if (e instanceof SyncError && e.status === 401) {
          const fresh = await get().refreshAccess();
          if (!fresh) throw new SyncError("Your session expired. Sign in again.");
          await client.resendVerification(fresh);
        } else {
          throw e;
        }
      }
      toast.success(tr("toast.auth.verificationSent"));
    } catch (e) {
      toast.error(e instanceof SyncError ? e.message : e instanceof Error ? e.message : String(e));
    }
  },

  async refreshAccess(opts) {
    const { refreshToken } = get();
    const url = await serverUrl();
    if (!url || !refreshToken) return null;
    const generation = accountGeneration();
    try {
      const res = await new AuthClient(url).refresh(refreshToken);
      const apply = async (): Promise<string | null> => {
        // A slower refresh response from the previous account must never replace
        // tokens established by a sign-in/sign-out that completed meanwhile.
        if (accountGeneration() !== generation) return null;
        await persistTokens(res.accessToken, res.refreshToken, res.user.email);
        set({ accessToken: res.accessToken, refreshToken: res.refreshToken, user: res.user });
        return res.accessToken;
      };
      return opts?.syncLaneHeld ? apply() : withAccountTransition(apply);
    } catch (e) {
      // Sign out ONLY when the server definitively rejects the refresh token
      // (401; 400 = malformed). A network drop or cold-start timeout must not
      // sign the user out — the caller's operation fails and retries later.
      if (e instanceof SyncError && (e.status === 400 || e.status === 401)) {
        await clearLocalAccount(set, {
          syncLaneHeld: opts?.syncLaneHeld ?? false,
          expectedGeneration: generation,
        });
      }
      return null;
    }
  },
}));

type SetFn = (partial: Partial<AuthState>) => void;

/** Best-effort background fetch of `/auth/me` after boot. A 401 falls through to
 *  the refresh path (which signs out on a dead refresh token); network errors —
 *  offline, cold-starting server — are ignored and the cached email stands. */
async function refreshUser(
  set: SetFn,
  get: () => AuthState,
  accessToken: string,
): Promise<void> {
  const generation = accountGeneration();
  const url = await serverUrl();
  if (!url) return;
  try {
    const { user } = await new AuthClient(url).me(accessToken);
    if (accountGeneration() !== generation || get().accessToken !== accessToken) return;
    set({ user });
  } catch (e) {
    // 401 → refresh; a successful refresh response already carries the full
    // user (incl. email_verified), and a dead refresh token signs out.
    if (
      e instanceof SyncError &&
      e.status === 401 &&
      accountGeneration() === generation &&
      get().accessToken === accessToken
    ) {
      await get().refreshAccess();
    }
    /* transient network/server error — keep the cached identity */
  }
}

async function authenticate(
  set: SetFn,
  mode: "signup" | "login",
  email: string,
  password: string,
): Promise<boolean> {
  const url = await serverUrl();
  if (!url) {
    toast.error(tr("toast.sync.setUrlFirst"));
    return false;
  }
  set({ busy: true, error: null });
  try {
    const client = new AuthClient(url);
    const res = await (mode === "signup"
      ? client.signup(email.trim(), password)
      : client.login(email.trim(), password));
    await establishAccount(set, res);
    toast.success(tr(mode === "signup" ? "toast.auth.accountCreated" : "toast.auth.signedIn"));
    return true;
  } catch (e) {
    const msg = e instanceof SyncError ? e.message : e instanceof Error ? e.message : String(e);
    set({ error: msg });
    toast.error(msg);
    return false;
  } finally {
    set({ busy: false });
  }
}

async function persistTokens(
  access: string | null,
  refresh: string | null,
  email: string | null,
): Promise<void> {
  const s = getServices().settings;
  await s.set(K_ACCESS, access ?? "");
  await s.set(K_REFRESH, refresh ?? "");
  await s.set(K_EMAIL, email ?? "");
}

interface AccountTokens {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

/** Establish a session only after the previous account's cursor and acks have
 * been cleared successfully. The whole transition owns the sync lane, so no
 * server or Google pass can write across the account boundary. */
async function establishAccount(set: SetFn, account: AccountTokens): Promise<void> {
  await withAccountTransition(async () => {
    await clearAccountSyncState();
    await persistTokens(account.accessToken, account.refreshToken, account.user.email);
    bumpAccountGeneration();
    set({ user: account.user, accessToken: account.accessToken, refreshToken: account.refreshToken });
  });
}

/** Drop the local session while holding the sync lane. A refresh rejection found
 * by the server client already owns the lane; every other caller acquires it.
 * Cleanup failures propagate, but tokens are still removed and the generation
 * still changes so a local storage error cannot leave a dead account active. */
async function clearLocalAccount(
  set: SetFn,
  opts?: { syncLaneHeld?: boolean; expectedGeneration?: number },
): Promise<void> {
  const clear = async (): Promise<void> => {
    // Ignore a stale refresh response from an account that has already changed.
    if (opts?.expectedGeneration != null && accountGeneration() !== opts.expectedGeneration) return;
    let firstError: unknown = null;
    try {
      await clearAccountSyncState();
    } catch (e) {
      firstError = e;
    }
    try {
      await persistTokens(null, null, null);
    } catch (e) {
      firstError ??= e;
    }
    bumpAccountGeneration();
    set({ user: null, accessToken: null, refreshToken: null, error: null });
    if (firstError) throw firstError;
  };
  if (opts?.syncLaneHeld) await clear();
  else await withAccountTransition(clear);
}
